import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProvider } from '@/contexts/AppContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthCallbackHandler } from '@/components/AuthCallbackHandler';
import { ScrollToTop } from '@/components/ScrollToTop';
import { HomePage } from '@/pages/HomePage';

// Lazy-loaded pages for code splitting
const SearchPage = React.lazy(() => import('@/pages/SearchPage').then(m => ({ default: m.SearchPage })));
const FavoritesPage = React.lazy(() => import('@/pages/FavoritesPage').then(m => ({ default: m.FavoritesPage })));
const HistoryPage = React.lazy(() => import('@/pages/HistoryPage').then(m => ({ default: m.HistoryPage })));
const PlayerPage = React.lazy(() => import('@/pages/PlayerPage').then(m => ({ default: m.PlayerPage })));
const BrowsePage = React.lazy(() => import('@/pages/BrowsePage').then(m => ({ default: m.BrowsePage })));
const OfflinePage = React.lazy(() => import('@/pages/OfflinePage').then(m => ({ default: m.OfflinePage })));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const DonationPage = React.lazy(() => import('@/pages/DonationPage').then(m => ({ default: m.DonationPage })));
const PrivacyPage = React.lazy(() => import('@/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const PlaylistsPage = React.lazy(() => import('@/pages/PlaylistsPage').then(m => ({ default: m.PlaylistsPage })));
const SharedPlaylistPage = React.lazy(() => import('@/pages/SharedPlaylistPage').then(m => ({ default: m.SharedPlaylistPage })));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const LoginPage = React.lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = React.lazy(() => import('@/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = React.lazy(() => import('@/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ImportPage = React.lazy(() => import('@/pages/ImportPage').then(m => ({ default: m.ImportPage })));
const SongPage = React.lazy(() => import('@/pages/SongPage').then(m => ({ default: m.SongPage })));
const AuthorsPage = React.lazy(() => import('@/pages/AuthorsPage').then(m => ({ default: m.AuthorsPage })));
const AuthorPage = React.lazy(() => import('@/pages/AuthorPage').then(m => ({ default: m.AuthorPage })));

// Lightweight loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AuthCallbackHandler />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/player" element={<PlayerPage />} />
                <Route path="/browse" element={<BrowsePage />} />
                <Route path="/offline" element={<OfflinePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/donate" element={<DonationPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/playlists" element={<PlaylistsPage />} />
                <Route path="/playlist/:shareId" element={<SharedPlaylistPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/import" element={<ImportPage />} />
                <Route path="/chant/:numero" element={<SongPage />} />
                {/* Author pages disabled for now
                <Route path="/auteurs" element={<AuthorsPage />} />
                <Route path="/auteur/:slug" element={<AuthorPage />} />
                */}
                {/* Route 404 - doit être la dernière */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;


